import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, RotateCcw, ZoomIn } from "lucide-react";

/**
 * Convert the user's crop area on the source image into a cropped JPEG Blob.
 * Uses a canvas so EXIF orientation/rotation/zoom are baked in.
 */
async function getCroppedBlob(imageSrc, cropArea, rotation = 0, outputW = 900) {
    const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = imageSrc;
    });

    const radians = (rotation * Math.PI) / 180;
    const sin = Math.abs(Math.sin(radians));
    const cos = Math.abs(Math.cos(radians));
    const bBoxW = image.width * cos + image.height * sin;
    const bBoxH = image.width * sin + image.height * cos;

    // Draw the rotated image on an intermediary canvas
    const tmp = document.createElement("canvas");
    tmp.width = bBoxW;
    tmp.height = bBoxH;
    const tctx = tmp.getContext("2d");
    tctx.translate(bBoxW / 2, bBoxH / 2);
    tctx.rotate(radians);
    tctx.drawImage(image, -image.width / 2, -image.height / 2);

    // Copy cropped area to output canvas, resizing to max outputW
    const scale = Math.min(1, outputW / cropArea.width);
    const outW = Math.round(cropArea.width * scale);
    const outH = Math.round(cropArea.height * scale);
    const out = document.createElement("canvas");
    out.width = outW;
    out.height = outH;
    const octx = out.getContext("2d");
    octx.drawImage(
        tmp,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        outW,
        outH
    );

    return await new Promise((resolve) =>
        out.toBlob(resolve, "image/jpeg", 0.9)
    );
}

export default function ImageCropper({
    open,
    src,
    aspect = 3 / 4,
    originalName = "image.jpg",
    onCancel,
    onCrop,
}) {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [pixels, setPixels] = useState(null);
    const [busy, setBusy] = useState(false);

    const onCropComplete = useCallback((_, px) => {
        setPixels(px);
    }, []);

    const confirm = async () => {
        if (!pixels || !src) return;
        setBusy(true);
        try {
            const blob = await getCroppedBlob(src, pixels, rotation);
            if (!blob) {
                onCancel?.();
                return;
            }
            const baseName = originalName.replace(/\.[^/.]+$/, "") || "image";
            const file = new File([blob], `${baseName}-cropped.jpg`, {
                type: "image/jpeg",
            });
            onCrop?.(file);
        } finally {
            setBusy(false);
        }
    };

    const reset = () => {
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
    };

    return (
        <AnimatePresence>
            {open && src && (
                <motion.div
                    data-testid="image-cropper"
                    className="fixed inset-0 z-[140] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        className="w-full max-w-lg bg-[#121212] rounded-3xl border border-white/10 overflow-hidden flex flex-col"
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                    >
                        <div className="flex items-center justify-between p-4 border-b border-white/10">
                            <div>
                                <div className="font-display text-lg uppercase tracking-tight">
                                    Crop image
                                </div>
                                <div className="text-[11px] text-white/40">
                                    Drag to position · pinch/scroll to zoom
                                </div>
                            </div>
                            <button
                                data-testid="cropper-close"
                                onClick={onCancel}
                                className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10"
                                aria-label="Close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="relative w-full aspect-[3/4] bg-black">
                            <Cropper
                                image={src}
                                crop={crop}
                                zoom={zoom}
                                rotation={rotation}
                                aspect={aspect}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onRotationChange={setRotation}
                                onCropComplete={onCropComplete}
                                objectFit="contain"
                                showGrid
                            />
                        </div>

                        <div className="p-4 space-y-3 border-t border-white/10">
                            <label className="flex items-center gap-3 text-xs text-white/70">
                                <ZoomIn size={14} className="kw-orange" />
                                <input
                                    data-testid="cropper-zoom"
                                    type="range"
                                    min={1}
                                    max={3}
                                    step={0.01}
                                    value={zoom}
                                    onChange={(e) =>
                                        setZoom(parseFloat(e.target.value))
                                    }
                                    className="flex-1 accent-[#FF5A00]"
                                />
                                <span className="w-10 text-right tabular-nums">
                                    {zoom.toFixed(1)}×
                                </span>
                            </label>
                            <div className="flex items-center justify-between gap-2">
                                <button
                                    data-testid="cropper-rotate"
                                    onClick={() =>
                                        setRotation((r) => (r + 90) % 360)
                                    }
                                    className="flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest rounded-lg border border-white/15 text-white/70 hover:text-white hover:border-white/40"
                                >
                                    <RotateCcw size={14} /> Rotate
                                </button>
                                <button
                                    data-testid="cropper-reset"
                                    onClick={reset}
                                    className="px-3 py-2 text-xs uppercase tracking-widest rounded-lg border border-white/10 text-white/50 hover:text-white"
                                >
                                    Reset
                                </button>
                                <div className="flex-1" />
                                <button
                                    data-testid="cropper-cancel"
                                    onClick={onCancel}
                                    className="px-4 py-2 text-xs uppercase tracking-widest rounded-lg border border-white/15 text-white/70 hover:text-white"
                                >
                                    Cancel
                                </button>
                                <button
                                    data-testid="cropper-apply"
                                    onClick={confirm}
                                    disabled={busy || !pixels}
                                    className="px-4 py-2 kw-orange-bg text-black font-bold rounded-lg uppercase tracking-widest text-xs flex items-center gap-1 disabled:opacity-60"
                                >
                                    <Check size={14} />
                                    {busy ? "Applying…" : "Apply"}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
